// features/procurement/ProcurementPage.tsx
/**
 * Procurement & Supply Chain Command Center — Executive UI/UX Redesign
 * Featuring Lifecycle Pipeline, Intelligent Auto-Requisitions, Dynamic Status Tabs,
 * and Fast Direct Actions.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import {
    Truck, FileText, RotateCcw, ClipboardList,
    ArrowRight, DollarSign, AlertCircle,
    CheckCircle2, Clock, Store, Zap, Package,
    ShieldCheck, ChevronRight, Search, Plus,
    RefreshCw, Filter, ArrowUpRight, TrendingUp,
    ExternalLink, AlertTriangle, Send, Eye,
    Building2, Star, CheckCircle, Ban
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getVendors,
    getRequisitions,
    getPurchaseOrders,
    getGRNs,
    getReturns,
    checkLowStock,
    autoRequisition,
    getProcurementErrorMessage,
} from './services/procurementApi';
import { useToastStore } from '@/store/toastStore';

// ============================================================
// CONSTANTS & BADGE FORMATTERS
// ============================================================

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
    draft: { label: 'Draft', bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
    pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    approved: { label: 'Approved', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
    sent: { label: 'Sent to Vendor', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
    partially_received: { label: 'Partially Received', bg: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-500' },
    fully_received: { label: 'Received', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    confirmed: { label: 'Confirmed', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    cancelled: { label: 'Cancelled', bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
    converted_to_po: { label: 'Converted to PO', bg: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-500' },
    rejected: { label: 'Rejected', bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
};

function StatusBadge({ status }: { status: string }) {
    const config = STATUS_CONFIG[status] || {
        label: status.replace(/_/g, ' '),
        bg: 'bg-slate-100',
        text: 'text-slate-700',
        dot: 'bg-slate-400',
    };

    return (
        <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold', config.bg, config.text)}>
            <span className={clsx('w-1.5 h-1.5 rounded-full', config.dot)} />
            {config.label}
        </span>
    );
}

// ============================================================
// MAIN PROCUREMENT DASHBOARD
// ============================================================

export function ProcurementPage() {
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const tenantId = user?.tenantId || '';
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // Active Operations Hub Tab
    const [activeTab, setActiveTab] = useState<'orders' | 'grns' | 'requisitions' | 'returns'>('orders');
    const [searchQuery, setSearchQuery] = useState('');

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: requisitions, isLoading: reqsLoading, refetch: refetchReqs } = useQuery({
        queryKey: ['dashboard-requisitions', tenantId],
        queryFn: async () => {
            const res = await getRequisitions(tenantId, { limit: 15 });
            return res.requisitions;
        },
        enabled: !!tenantId,
        retry: 1,
    });

    const { data: orders, isLoading: ordersLoading, refetch: refetchOrders } = useQuery({
        queryKey: ['dashboard-orders', tenantId],
        queryFn: async () => {
            const res = await getPurchaseOrders(tenantId, { limit: 15 });
            return res.pos;
        },
        enabled: !!tenantId,
        retry: 1,
    });

    const { data: grns, isLoading: grnsLoading, refetch: refetchGrns } = useQuery({
        queryKey: ['dashboard-grns', tenantId],
        queryFn: () => getGRNs(tenantId, { limit: 15 }),
        enabled: !!tenantId,
        retry: 1,
    });

    const { data: returns, isLoading: returnsLoading, refetch: refetchReturns } = useQuery({
        queryKey: ['dashboard-returns', tenantId],
        queryFn: () => getReturns(tenantId, { limit: 15 }),
        enabled: !!tenantId,
        retry: 1,
    });

    const { data: vendors, isLoading: vendorsLoading, refetch: refetchVendors } = useQuery({
        queryKey: ['dashboard-vendors', tenantId],
        queryFn: () => getVendors(tenantId),
        enabled: !!tenantId,
        retry: 1,
    });

    const { data: lowStock, refetch: refetchLowStock } = useQuery({
        queryKey: ['dashboard-low-stock', tenantId],
        queryFn: () => checkLowStock(tenantId),
        enabled: !!tenantId,
        retry: 1,
        refetchInterval: 60_000,
    });

    // Auto-Requisition Mutation
    const autoReqMutation = useMutation({
        mutationFn: () => autoRequisition(tenantId, selectedBranchId || undefined),
        onSuccess: (data: any) => {
            addToast({
                type: 'success',
                title: 'Auto-Requisition Generated',
                message: `Created requisition with ${data?.productsRequested || data?.itemCount || 0} low-stock items!`,
            });
            queryClient.invalidateQueries({ queryKey: ['dashboard-requisitions'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-low-stock'] });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Auto-Requisition Failed',
                message: getProcurementErrorMessage(err),
            });
        },
    });

    const handleRefreshAll = () => {
        refetchOrders();
        refetchGrns();
        refetchReqs();
        refetchReturns();
        refetchVendors();
        refetchLowStock();
        addToast({
            type: 'info',
            title: 'Procurement Synced',
            message: 'All procurement lists and low stock items updated.',
        });
    };

    // ============================================================
    // METRICS CALCULATIONS
    // ============================================================

    const reqsList = requisitions || [];
    const ordersList = orders || [];
    const grnsList = grns || [];
    const returnsList = returns || [];
    const vendorsList = vendors || [];
    const lowStockCount = lowStock?.lowStockCount || 0;

    const pendingReqs = reqsList.filter((r: any) => r.status === 'pending_approval' || r.status === 'draft').length;
    const activeOrders = ordersList.filter((o: any) => ['pending_approval', 'approved', 'sent', 'partially_received'].includes(o.status)).length;
    const pendingGrns = grnsList.filter((g: any) => g.status === 'draft').length;
    const confirmedGrns = grnsList.filter((g: any) => g.status === 'confirmed').length;

    const totalSpend = ordersList
        .filter((o: any) => o.status !== 'cancelled')
        .reduce((sum: number, o: any) => sum + parseFloat(o.totalAmount || '0'), 0);

    const totalReturnValue = returnsList
        .reduce((sum: number, r: any) => sum + parseFloat(r.totalAmount || '0'), 0);

    const formatNaira = (val: number) => `₦${val.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

    // Filter items based on active tab & search query
    const filteredTabItems = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (activeTab === 'orders') {
            return ordersList.filter((o: any) =>
                !query ||
                o.poNumber?.toLowerCase().includes(query) ||
                o.vendorName?.toLowerCase().includes(query) ||
                o.status?.toLowerCase().includes(query)
            );
        }
        if (activeTab === 'grns') {
            return grnsList.filter((g: any) =>
                !query ||
                g.grnNumber?.toLowerCase().includes(query) ||
                g.vendorName?.toLowerCase().includes(query) ||
                g.poNumber?.toLowerCase().includes(query) ||
                g.status?.toLowerCase().includes(query)
            );
        }
        if (activeTab === 'requisitions') {
            return reqsList.filter((r: any) =>
                !query ||
                r.requisitionNumber?.toLowerCase().includes(query) ||
                r.branchName?.toLowerCase().includes(query) ||
                r.status?.toLowerCase().includes(query)
            );
        }
        if (activeTab === 'returns') {
            return returnsList.filter((r: any) =>
                !query ||
                r.returnNumber?.toLowerCase().includes(query) ||
                r.vendorName?.toLowerCase().includes(query) ||
                r.reason?.toLowerCase().includes(query) ||
                r.status?.toLowerCase().includes(query)
            );
        }
        return [];
    }, [activeTab, searchQuery, ordersList, grnsList, reqsList, returnsList]);

    const isGlobalLoading = reqsLoading && ordersLoading && grnsLoading;

    if (isGlobalLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[70vh] gap-4">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                    className="w-14 h-14 rounded-2xl border-4 border-teal-200 border-t-teal-600 shadow-md"
                />
                <p className="text-sm font-medium text-slate-500">Loading Procurement Command Center...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50/60 pb-16">
            {/* ============================================================ */}
            {/* EXECUTIVE HEADER                                             */}
            {/* ============================================================ */}
            <div className="bg-white/95 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1680px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl shadow-lg shadow-teal-500/20 text-white">
                                <Truck className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="flex items-center gap-3">
                                    <h1 className="text-2xl font-black text-slate-900 tracking-tight">Procurement & Supply Chain</h1>
                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">
                                        Enterprise
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                                    <span>{vendorsList.length} Active Suppliers</span>
                                    <span>•</span>
                                    <span>{ordersList.length} Orders</span>
                                    <span>•</span>
                                    <span>{grnsList.length} Goods Receipts</span>
                                </p>
                            </div>
                        </div>

                        {/* Top Action Cluster */}
                        <div className="flex items-center gap-2.5 flex-wrap">
                            <button
                                onClick={handleRefreshAll}
                                className="p-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all font-medium text-xs flex items-center gap-1.5"
                                title="Refresh Procurement Data"
                                type="button"
                            >
                                <RefreshCw size={15} />
                                <span className="hidden sm:inline">Sync</span>
                            </button>

                            <button
                                onClick={() => navigate('/procurement/requisitions/new')}
                                className="px-3.5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all font-semibold text-xs flex items-center gap-1.5 shadow-sm"
                                type="button"
                            >
                                <ClipboardList size={15} className="text-blue-600" />
                                <span>Requisition</span>
                            </button>

                            <button
                                onClick={() => navigate('/procurement/returns/new')}
                                className="px-3.5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all font-semibold text-xs flex items-center gap-1.5 shadow-sm"
                                type="button"
                            >
                                <RotateCcw size={15} className="text-rose-600" />
                                <span>Return Items</span>
                            </button>

                            <button
                                onClick={() => navigate('/procurement/grns/new')}
                                className="px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-sky-600 text-white rounded-xl hover:from-cyan-700 hover:to-sky-700 transition-all font-semibold text-xs flex items-center gap-1.5 shadow-md shadow-cyan-500/20"
                                type="button"
                            >
                                <Truck size={15} />
                                <span>Receive Goods (GRN)</span>
                            </button>

                            <button
                                onClick={() => navigate('/procurement/orders/new')}
                                className="px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:from-teal-700 hover:to-emerald-700 transition-all font-semibold text-xs flex items-center gap-1.5 shadow-md shadow-teal-500/25"
                                type="button"
                            >
                                <Plus size={16} />
                                <span>New Purchase Order</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1680px] mx-auto px-6 lg:px-8 pt-6 space-y-6">

                {/* ============================================================ */}
                {/* LOW STOCK INTELLIGENCE ALERT BANNER                          */}
                {/* ============================================================ */}
                {lowStockCount > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border border-amber-300 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm"
                    >
                        <div className="flex items-center gap-3.5">
                            <div className="p-2.5 bg-amber-500 text-white rounded-xl shadow-md shadow-amber-500/30">
                                <AlertTriangle size={22} />
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-amber-950 flex items-center gap-2">
                                    <span>{lowStockCount} Products are Below Reorder Point</span>
                                    <span className="px-2 py-0.5 text-[10px] font-bold bg-amber-200 text-amber-900 rounded-full uppercase">Action Required</span>
                                </h4>
                                <p className="text-xs text-amber-800/80 mt-0.5">
                                    Warehouse inventory is below designated safety thresholds. Generate purchase requisitions to prevent stockouts.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2.5 w-full sm:w-auto">
                            <button
                                onClick={() => navigate('/inventory')}
                                className="px-3.5 py-2 text-xs font-semibold text-amber-900 bg-amber-100 hover:bg-amber-200 rounded-xl transition"
                                type="button"
                            >
                                View Inventory
                            </button>
                            <button
                                onClick={() => autoReqMutation.mutate()}
                                disabled={autoReqMutation.isPending}
                                className="px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl hover:from-amber-700 hover:to-orange-700 transition font-bold text-xs shadow-md shadow-amber-600/25 flex items-center gap-2 disabled:opacity-50"
                                type="button"
                            >
                                {autoReqMutation.isPending ? <Spinner size="xs" variant="white" /> : <Zap size={14} />}
                                <span>Auto-Requisition All</span>
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* ============================================================ */}
                {/* SUPPLY CHAIN LIFECYCLE PIPELINE                              */}
                {/* ============================================================ */}
                <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Procurement Workflow Pipeline</h3>
                            <p className="text-xs text-slate-400">Click any step to inspect documents at that stage</p>
                        </div>
                        <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full border border-teal-200/60">
                            Live Stage Tracking
                        </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {/* Step 1: Requisitions */}
                        <motion.div
                            whileHover={{ y: -2 }}
                            onClick={() => { setActiveTab('requisitions'); }}
                            className={clsx(
                                'p-4 rounded-xl border transition-all cursor-pointer text-left',
                                activeTab === 'requisitions'
                                    ? 'border-blue-500 bg-blue-50/40 shadow-sm'
                                    : 'border-slate-200 bg-slate-50/60 hover:bg-white hover:border-blue-300'
                            )}
                        >
                            <div className="flex items-center justify-between text-blue-600 mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider">1. Requisition</span>
                                <ClipboardList size={18} />
                            </div>
                            <p className="text-2xl font-black text-slate-900">{pendingReqs}</p>
                            <p className="text-xs text-slate-500 mt-0.5">Pending Review</p>
                        </motion.div>

                        {/* Step 2: Active POs */}
                        <motion.div
                            whileHover={{ y: -2 }}
                            onClick={() => { setActiveTab('orders'); }}
                            className={clsx(
                                'p-4 rounded-xl border transition-all cursor-pointer text-left',
                                activeTab === 'orders'
                                    ? 'border-teal-500 bg-teal-50/40 shadow-sm'
                                    : 'border-slate-200 bg-slate-50/60 hover:bg-white hover:border-teal-300'
                            )}
                        >
                            <div className="flex items-center justify-between text-teal-600 mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider">2. Purchase Orders</span>
                                <FileText size={18} />
                            </div>
                            <p className="text-2xl font-black text-slate-900">{activeOrders}</p>
                            <p className="text-xs text-slate-500 mt-0.5">Active / Dispatched</p>
                        </motion.div>

                        {/* Step 3: Pending GRN */}
                        <motion.div
                            whileHover={{ y: -2 }}
                            onClick={() => { setActiveTab('grns'); }}
                            className={clsx(
                                'p-4 rounded-xl border transition-all cursor-pointer text-left',
                                activeTab === 'grns'
                                    ? 'border-cyan-500 bg-cyan-50/40 shadow-sm'
                                    : 'border-slate-200 bg-slate-50/60 hover:bg-white hover:border-cyan-300'
                            )}
                        >
                            <div className="flex items-center justify-between text-cyan-600 mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider">3. Goods Received</span>
                                <Truck size={18} />
                            </div>
                            <p className="text-2xl font-black text-slate-900">{pendingGrns}</p>
                            <p className="text-xs text-slate-500 mt-0.5">Awaiting Confirmation</p>
                        </motion.div>

                        {/* Step 4: Confirmed Receipts */}
                        <motion.div
                            whileHover={{ y: -2 }}
                            onClick={() => { setActiveTab('grns'); }}
                            className="p-4 rounded-xl border border-slate-200 bg-slate-50/60 hover:bg-white hover:border-emerald-300 transition-all cursor-pointer text-left"
                        >
                            <div className="flex items-center justify-between text-emerald-600 mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider">4. In Stock</span>
                                <CheckCircle2 size={18} />
                            </div>
                            <p className="text-2xl font-black text-slate-900">{confirmedGrns}</p>
                            <p className="text-xs text-slate-500 mt-0.5">Confirmed GRNs</p>
                        </motion.div>

                        {/* Step 5: Returns */}
                        <motion.div
                            whileHover={{ y: -2 }}
                            onClick={() => { setActiveTab('returns'); }}
                            className={clsx(
                                'p-4 rounded-xl border transition-all cursor-pointer text-left',
                                activeTab === 'returns'
                                    ? 'border-rose-500 bg-rose-50/40 shadow-sm'
                                    : 'border-slate-200 bg-slate-50/60 hover:bg-white hover:border-rose-300'
                            )}
                        >
                            <div className="flex items-center justify-between text-rose-600 mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider">5. Claims / Returns</span>
                                <RotateCcw size={18} />
                            </div>
                            <p className="text-2xl font-black text-slate-900">{returnsList.length}</p>
                            <p className="text-xs text-slate-500 mt-0.5">Vendor Credit Notes</p>
                        </motion.div>
                    </div>
                </div>

                {/* ============================================================ */}
                {/* TOP FINANCIAL & OPERATIONAL METRIC CARDS                     */}
                {/* ============================================================ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Total Spend */}
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Purchase Spend</span>
                            <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600">
                                <DollarSign size={20} />
                            </div>
                        </div>
                        <p className="text-2xl font-black text-slate-900 mt-2 truncate">{formatNaira(totalSpend)}</p>
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                            <TrendingUp size={13} className="text-emerald-500" />
                            <span>Across {ordersList.length} total orders</span>
                        </p>
                    </div>

                    {/* Active Suppliers */}
                    <div
                        onClick={() => navigate('/vendors')}
                        className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Suppliers Directory</span>
                            <div className="p-2.5 rounded-xl bg-amber-50 text-amber-600 group-hover:scale-105 transition-transform">
                                <Store size={20} />
                            </div>
                        </div>
                        <p className="text-2xl font-black text-slate-900 mt-2">{vendorsList.length}</p>
                        <div className="flex items-center justify-between mt-1 text-xs text-teal-600 font-semibold">
                            <span>Manage Suppliers</span>
                            <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                    </div>

                    {/* Receipts Awaiting Confirmation */}
                    <div
                        onClick={() => setActiveTab('grns')}
                        className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Goods Receipts</span>
                            <div className="p-2.5 rounded-xl bg-cyan-50 text-cyan-600 group-hover:scale-105 transition-transform">
                                <Package size={20} />
                            </div>
                        </div>
                        <p className={clsx('text-2xl font-black mt-2', pendingGrns > 0 ? 'text-amber-600' : 'text-slate-900')}>
                            {pendingGrns}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                            {pendingGrns > 0 ? 'Requires stock confirmation' : 'All receipts up to date'}
                        </p>
                    </div>

                    {/* Vendor Returns */}
                    <div
                        onClick={() => setActiveTab('returns')}
                        className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Vendor Returns</span>
                            <div className="p-2.5 rounded-xl bg-rose-50 text-rose-600 group-hover:scale-105 transition-transform">
                                <RotateCcw size={20} />
                            </div>
                        </div>
                        <p className="text-2xl font-black text-slate-900 mt-2">{returnsList.length}</p>
                        <p className="text-xs text-rose-600 font-medium mt-1">
                            {totalReturnValue > 0 ? `${formatNaira(totalReturnValue)} returned` : 'No active return claims'}
                        </p>
                    </div>
                </div>

                {/* ============================================================ */}
                {/* OPERATIONS HUB & DOCUMENT TABLES                             */}
                {/* ============================================================ */}
                <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                    {/* Tabs & Search Navigation Bar */}
                    <div className="px-6 py-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-50/50">
                        {/* Tab Switcher */}
                        <div className="flex items-center gap-1.5 p-1 bg-slate-200/60 rounded-xl">
                            <button
                                onClick={() => setActiveTab('orders')}
                                className={clsx(
                                    'px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2',
                                    activeTab === 'orders' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                                )}
                                type="button"
                            >
                                <FileText size={14} />
                                <span>Purchase Orders</span>
                                <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-slate-100 text-slate-600">
                                    {ordersList.length}
                                </span>
                            </button>

                            <button
                                onClick={() => setActiveTab('grns')}
                                className={clsx(
                                    'px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2',
                                    activeTab === 'grns' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                                )}
                                type="button"
                            >
                                <Truck size={14} />
                                <span>Goods Received (GRN)</span>
                                <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-slate-100 text-slate-600">
                                    {grnsList.length}
                                </span>
                            </button>

                            <button
                                onClick={() => setActiveTab('requisitions')}
                                className={clsx(
                                    'px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2',
                                    activeTab === 'requisitions' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                                )}
                                type="button"
                            >
                                <ClipboardList size={14} />
                                <span>Requisitions</span>
                                <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-slate-100 text-slate-600">
                                    {reqsList.length}
                                </span>
                            </button>

                            <button
                                onClick={() => setActiveTab('returns')}
                                className={clsx(
                                    'px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2',
                                    activeTab === 'returns' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                                )}
                                type="button"
                            >
                                <RotateCcw size={14} />
                                <span>Returns</span>
                                <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-slate-100 text-slate-600">
                                    {returnsList.length}
                                </span>
                            </button>
                        </div>

                        {/* Search Bar & View All Button */}
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={`Search ${activeTab}...`}
                                    className="pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 w-48 sm:w-64"
                                />
                            </div>

                            <button
                                onClick={() => {
                                    if (activeTab === 'orders') navigate('/procurement/orders');
                                    if (activeTab === 'grns') navigate('/procurement/grns');
                                    if (activeTab === 'requisitions') navigate('/procurement/requisitions');
                                    if (activeTab === 'returns') navigate('/procurement/returns');
                                }}
                                className="px-3.5 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded-xl text-xs font-semibold transition flex items-center gap-1"
                                type="button"
                            >
                                <span>Full Table</span>
                                <ArrowRight size={13} />
                            </button>
                        </div>
                    </div>

                    {/* Table View */}
                    <div className="overflow-x-auto">
                        {filteredTabItems.length === 0 ? (
                            <div className="py-16 text-center">
                                <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3 text-slate-400">
                                    <Package size={26} />
                                </div>
                                <h4 className="text-base font-bold text-slate-800">No {activeTab} Found</h4>
                                <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                                    {searchQuery ? `No results matching "${searchQuery}". Try a different search term.` : `No records created yet in this section.`}
                                </p>
                            </div>
                        ) : (
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/70">
                                        <th className="py-3.5 px-6">Reference</th>
                                        <th className="py-3.5 px-6">{activeTab === 'requisitions' ? 'Branch' : 'Supplier / Vendor'}</th>
                                        <th className="py-3.5 px-6">Date</th>
                                        <th className="py-3.5 px-6">Status</th>
                                        {activeTab === 'orders' && <th className="py-3.5 px-6 text-right">Amount</th>}
                                        <th className="py-3.5 px-6 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm">
                                    {filteredTabItems.map((item: any) => (
                                        <tr
                                            key={item.id}
                                            className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                                            onClick={() => {
                                                if (activeTab === 'orders') navigate(`/procurement/orders/${item.id}`);
                                                if (activeTab === 'grns') navigate(`/procurement/grns/${item.id}`);
                                                if (activeTab === 'requisitions') navigate(`/procurement/requisitions/${item.id}`);
                                                if (activeTab === 'returns') navigate(`/procurement/returns/${item.id}`);
                                            }}
                                        >
                                            {/* Reference Number */}
                                            <td className="py-4 px-6 font-mono font-bold text-slate-900 flex items-center gap-2">
                                                <span>
                                                    {item.poNumber || item.grnNumber || item.requisitionNumber || item.returnNumber}
                                                </span>
                                                {item.poNumber && activeTab === 'grns' && (
                                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-100 text-slate-500 border border-slate-200">
                                                        PO: {item.poNumber}
                                                    </span>
                                                )}
                                            </td>

                                            {/* Entity / Supplier */}
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-7 h-7 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                        {(item.vendorName || item.branchName || 'S').slice(0, 1).toUpperCase()}
                                                    </div>
                                                    <span className="font-medium text-slate-800">
                                                        {item.vendorName || item.branchName || 'Standard Supplier'}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Date */}
                                            <td className="py-4 px-6 text-xs text-slate-500">
                                                {formatDate(item.createdAt)}
                                            </td>

                                            {/* Status Badge */}
                                            <td className="py-4 px-6">
                                                <StatusBadge status={item.status || 'draft'} />
                                            </td>

                                            {/* Total Amount (for orders) */}
                                            {activeTab === 'orders' && (
                                                <td className="py-4 px-6 text-right font-black text-slate-900">
                                                    {formatNaira(parseFloat(item.totalAmount || '0'))}
                                                </td>
                                            )}

                                            {/* Direct Action */}
                                            <td className="py-4 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-2">
                                                    {activeTab === 'orders' && (item.status === 'sent' || item.status === 'partially_received') && (
                                                        <button
                                                            onClick={() => navigate(`/procurement/grns/new?poId=${item.id}`)}
                                                            className="px-3 py-1 text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 hover:bg-cyan-100 rounded-lg transition flex items-center gap-1 shadow-sm"
                                                            type="button"
                                                        >
                                                            <Truck size={12} />
                                                            <span>Receive</span>
                                                        </button>
                                                    )}

                                                    {activeTab === 'grns' && item.status === 'confirmed' && (
                                                        <button
                                                            onClick={() => navigate(`/procurement/returns/new?grnId=${item.id}`)}
                                                            className="px-3 py-1 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 rounded-lg transition flex items-center gap-1 shadow-sm"
                                                            type="button"
                                                        >
                                                            <RotateCcw size={12} />
                                                            <span>Return</span>
                                                        </button>
                                                    )}

                                                    <button
                                                        onClick={() => {
                                                            if (activeTab === 'orders') navigate(`/procurement/orders/${item.id}`);
                                                            if (activeTab === 'grns') navigate(`/procurement/grns/${item.id}`);
                                                            if (activeTab === 'requisitions') navigate(`/procurement/requisitions/${item.id}`);
                                                            if (activeTab === 'returns') navigate(`/procurement/returns/${item.id}`);
                                                        }}
                                                        className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
                                                        type="button"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* ============================================================ */}
                {/* VENDORS LEADERBOARD & RECENT SUPPLIERS                       */}
                {/* ============================================================ */}
                <div className="bg-white rounded-2xl border border-slate-200/90 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-amber-100 text-amber-700">
                                <Store size={18} />
                            </div>
                            <div>
                                <h3 className="font-bold text-sm text-slate-900">Top Suppliers & Partners</h3>
                                <p className="text-xs text-slate-400">High-volume suppliers and delivery reliability</p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/vendors')}
                            className="text-xs font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1"
                            type="button"
                        >
                            <span>Manage All Vendors</span>
                            <ArrowRight size={13} />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {vendorsList.slice(0, 3).map((v: any) => (
                            <div
                                key={v.id}
                                onClick={() => navigate(`/vendors/${v.id}`)}
                                className="p-4 rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer bg-slate-50/50 flex flex-col justify-between"
                            >
                                <div>
                                    <div className="flex items-start justify-between">
                                        <h4 className="font-bold text-sm text-slate-900">{v.name}</h4>
                                        <div className="flex items-center gap-0.5 text-amber-500">
                                            <Star size={12} className="fill-amber-400 text-amber-400" />
                                            <span className="text-xs font-bold text-slate-700">{v.rating || 5}.0</span>
                                        </div>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">{v.category || 'General Supplies'}</p>
                                </div>

                                <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                                    <span>{v.phone || v.email || 'Contact on file'}</span>
                                    <span className="font-bold text-teal-700 hover:underline">View Profile</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
}